import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { CrmInjectionToken, CrmObjectService } from '@galaxy/crm/static';
import { CustomObjectType, FieldSchemaInterface } from '@vendasta/crm';
import {
  DynamicVariableService as DynamicVariableServiceInterface,
  Category,
  DynamicField,
  ExtraCategoryOption,
} from '@galaxy/email-ui/builder-v3';
import { FeatureFlagService } from '../../core/feature-flag.service';
import { partnerId } from '../../../globals';
import { PROMOTIONS_NAMESPACE_TOKEN } from '@galaxy/promotions/core';
import { fetchActivePromotions$ } from './promotions-util';
import { resolvePromotionChipText } from '@galaxy/email-ui/email-builder';

const ACTIVE_PROMOTIONS_PICKER_ID = 'activePromotions';
const PROMOTION_ID_PLACEHOLDER = '<promotion-id>';


interface CrmDynamicContent {
  displayName: string;
  templateReplacementTag: string;
}

interface CustomObjectFieldsByType {
  type: CustomObjectType;
  fields: Record<string, CrmDynamicContent>;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicVariableService implements DynamicVariableServiceInterface {
  private readonly crmObjectService = inject(CrmObjectService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly crmConfig = inject(CrmInjectionToken);

  private readonly activePromotions$: Observable<ExtraCategoryOption[]> | null = (() => {
    const namespaceToken = inject(PROMOTIONS_NAMESPACE_TOKEN, { optional: true });
    if (!namespaceToken) return null;
    const namespace$ = typeof namespaceToken === 'string' ? of(namespaceToken) : (namespaceToken as Observable<string>);
    return fetchActivePromotions$(namespace$).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
  })();

  private readonly objectTypeTags: Record<string, string> = {
    Company: 'crmCampaignCompanyFields',
    Contact: 'crmCampaignContactFields',
    CustomObject: 'crmCustomObjectFields',
  };

  private readonly dynamicFieldSpanStyle =
    'padding: 4px 12px; background-color: #e0e0e0; border-radius: 16px; font-size: 13px; color: #616161; white-space: nowrap;';

  private decodeEncodedTemplateTag(tag: string): string {
    if (!/%[0-9A-Fa-f]{2}/.test(tag)) {
      return tag;
    }
    try {
      const innerContent = tag.slice(2, -2);
      const decodedInnerContent = decodeURIComponent(innerContent);
      return `{{${decodedInnerContent}}}`;
    } catch {
      return tag;
    }
  }

  private isInsideHtmlTag(source: string, index: number): boolean {
    const lastOpen = source.lastIndexOf('<', index);
    if (lastOpen === -1) {
      return false;
    }
    const lastClose = source.lastIndexOf('>', index);
    return lastClose < lastOpen;
  }

  readonly contactFields$ = this.crmObjectService.listFieldSchemas('Contact', undefined, { pageSize: 200 }).pipe(
    map((resp) => resp.fieldSchemas || []),
    map((fieldSchemas) => {
      return fieldSchemas.reduce(
        (fieldsMap, schema) => {
          fieldsMap[schema.fieldId] = {
            displayName: schema.fieldName,
            templateReplacementTag: `${this.objectTypeTags['Contact']}['__Contact_ID__', '${schema.fieldId}']`,
          } as CrmDynamicContent;
          return fieldsMap;
        },
        {} as Record<string, CrmDynamicContent>,
      );
    }),
    catchError((_) => {
      console.error('Failed to fetch contact fields for email-builder');
      return of({} as Record<string, CrmDynamicContent>);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly associatedCompanyFields$ = this.crmObjectService
    .listFieldSchemas('Company', undefined, { pageSize: 200 })
    .pipe(
      map((resp) => resp.fieldSchemas || []),
      map((fieldSchemas) => {
        return fieldSchemas.reduce(
          (fieldsMap, schema) => {
            fieldsMap[schema.fieldId] = {
              displayName: schema.fieldName,
              templateReplacementTag: `${this.objectTypeTags['Company']}['__Contact_ID__', '${schema.fieldId}']`,
            } as CrmDynamicContent;
            return fieldsMap;
          },
          {} as Record<string, CrmDynamicContent>,
        );
      }),
      catchError((_) => {
        console.error('Failed to fetch company fields for email-builder');
        return of({} as Record<string, CrmDynamicContent>);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  readonly customObjectFields$: Observable<CustomObjectFieldsByType[]> = (
    this.crmConfig.customObjectTypes$ ?? of<CustomObjectType[]>([])
  ).pipe(
    switchMap((types) =>
      !types || types.length === 0 ? of([]) : combineLatest(types.map((type) => this.loadCustomObjectFields$(type))),
    ),
    catchError(() => {
      console.error('Failed to fetch custom-object types for email-builder');
      return of<CustomObjectFieldsByType[]>([]);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private loadCustomObjectFields$(type: CustomObjectType): Observable<CustomObjectFieldsByType> {
    return this.crmObjectService.listFieldSchemas('CustomObject', type.customObjectTypeId, { pageSize: 200 }).pipe(
      map((resp) => ({
        type,
        fields: this.buildFieldsMap(
          resp.fieldSchemas || [],
          (fieldId) => `${this.objectTypeTags['CustomObject']}['${type.customObjectTypeId}', '${fieldId}']`,
        ),
      })),
      catchError(() => {
        console.error(`Failed to fetch custom-object fields for ${type.customObjectTypeId}`);
        return of({ type, fields: {} });
      }),
    );
  }

  private toCustomObjectCategory(entry: CustomObjectFieldsByType): Category {
    const fields: DynamicField[] = Object.values(entry.fields)
      .map((field) => ({
        id: field.templateReplacementTag,
        label: field.displayName,
        templateTag: `{{ ${field.templateReplacementTag} }}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      id: entry.type.customObjectTypeId,
      label: entry.type.pluralObjectName || entry.type.singularObjectName || 'Custom object',
      fields,
    };
  }

  private buildFieldsMap(
    fieldSchemas: FieldSchemaInterface[],
    buildTemplateReplacementTag: (fieldId: string) => string,
  ): Record<string, CrmDynamicContent> {
    return fieldSchemas.reduce(
      (fieldsMap, schema) => {
        fieldsMap[schema.fieldId] = {
          displayName: schema.fieldName,
          templateReplacementTag: buildTemplateReplacementTag(schema.fieldId),
        };
        return fieldsMap;
      },
      {} as Record<string, CrmDynamicContent>,
    );
  }

  private readonly allFields$ = combineLatest([
    this.associatedCompanyFields$,
    this.contactFields$,
    this.customObjectFields$,
  ]).pipe(
    map(([companyFields, contactFields, customObjectEntries]) => {
      const customObjectFieldMap: Record<string, CrmDynamicContent> = {};
      for (const entry of customObjectEntries) {
        Object.assign(customObjectFieldMap, entry.fields);
      }
      return { ...companyFields, ...contactFields, ...customObjectFieldMap };
    }),
  );

  private readonly promotionsEnabled$ = partnerId
    ? this.featureFlagService.checkFeatureFlag(partnerId, '', 'custom_form_promo_section').pipe(
        catchError(() => of(false)),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
    : of(false);

  private readonly couponCategory: Category = {
    id: 'coupon',
    label: 'Coupon',
    fields: [
      { id: 'couponCode', label: 'Coupon code', templateTag: '{{ couponCode }}' },
      { id: 'couponQrCode', label: 'Coupon QR code', templateTag: '{{ couponQrCode }}' },
      { id: 'couponImage', label: 'Coupon image', templateTag: '{{ couponImage }}' },
      { id: 'promotionName', label: 'Promotion name', templateTag: '{{ promotionName }}' },
      { id: 'promotionDescription', label: 'Promotion description', templateTag: '{{ promotionDescription }}' },
      { id: 'promotionStartDate', label: 'Promotion start date', templateTag: '{{ promotionStartDate }}' },
      { id: 'promotionEndDate', label: 'Promotion end date', templateTag: '{{ promotionEndDate }}' },
      { id: 'promotionOfferings', label: 'Promotion offerings', templateTag: '{{ promotionOfferings }}' },
    ],
  };

  private readonly promotionCategory: Category = {
    id: 'promotion',
    label: 'Promotion',
    fields: [
      {
        id: 'selectedPromotionName',
        label: 'Promotion name',
        templateTag: "{{ selectedPromotion['<promotion-id>, name'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
      {
        id: 'selectedPromotionDescription',
        label: 'Promotion description',
        templateTag: "{{ selectedPromotion['<promotion-id>, description'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
      {
        id: 'selectedPromotionStartDate',
        label: 'Promotion start date',
        templateTag: "{{ selectedPromotion['<promotion-id>, startDate'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
      {
        id: 'selectedPromotionEndDate',
        label: 'Promotion end date',
        templateTag: "{{ selectedPromotion['<promotion-id>, endDate'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
      {
        id: 'selectedPromotionOfferings',
        label: 'Promotion offering',
        templateTag: "{{ selectedPromotion['<promotion-id>, offerings'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
      {
        id: 'selectedPromotionImage',
        label: 'Promotion image',
        templateTag: "{{ selectedPromotionImage['<promotion-id>'] }}",
        extraCategoryField: { pickerId: ACTIVE_PROMOTIONS_PICKER_ID, templatePlaceholder: PROMOTION_ID_PLACEHOLDER },
      },
    ],
  };

  getExtraCategoryOptions$(pickerId: string): Observable<ExtraCategoryOption[]> {
    if (pickerId !== ACTIVE_PROMOTIONS_PICKER_ID || !this.activePromotions$) return of([]);
    return this.activePromotions$;
  }

  getDynamicVariableOptions$(): Observable<Category[]> {
    const accountCategory: Category = {
      id: 'account',
      label: 'Account details',
      fields: [
        { id: 'businessName', label: 'Business name', templateTag: '{{ businessName }}' },
        { id: 'businessStreetAddress', label: 'Business street address', templateTag: '{{ businessStreetAddress }}' },
        { id: 'businessStreetAddress2', label: 'Business street address 2', templateTag: '{{ businessStreetAddress2 }}' },
        { id: 'businessCity', label: 'Business city', templateTag: '{{ businessCity }}' },
        { id: 'businessState', label: 'Business state', templateTag: '{{ businessState }}' },
        { id: 'businessCountry', label: 'Business country', templateTag: '{{ businessCountry }}' },
        { id: 'businessPostalCode', label: 'Business postal code', templateTag: '{{ businessPostalCode }}' },
        { id: 'businessPhoneNumber', label: 'Business phone number', templateTag: '{{ businessPhoneNumber }}' },
        { id: 'businessWebsite', label: 'Business website', templateTag: '{{ businessWebsite }}' },
      ],
    };

    return combineLatest([
      this.contactFields$,
      this.associatedCompanyFields$,
      this.customObjectFields$,
      this.promotionsEnabled$,
    ]).pipe(
      map(([contactFieldsMap, companyFieldsMap, customObjectEntries, promotionsEnabled]) => {
        const hasContactError = Object.keys(contactFieldsMap).length === 0;
        const hasCompanyError = Object.keys(companyFieldsMap).length === 0;

        const customObjectCategories: Category[] = customObjectEntries
          .filter((entry) => Object.keys(entry.fields).length > 0)
          .map((entry) => this.toCustomObjectCategory(entry))
          .sort((a, b) => a.label.localeCompare(b.label));

        if (hasContactError && hasCompanyError) {
          const categories: Category[] = [accountCategory, ...customObjectCategories];
          if (promotionsEnabled) {
            categories.push(this.couponCategory);
            categories.push(this.promotionCategory);
          }
          return categories;
        }

        const contactFields: DynamicField[] = Object.values(contactFieldsMap)
          .map((field) => ({
            id: field.templateReplacementTag,
            label: field.displayName,
            templateTag: `{{ ${field.templateReplacementTag} }}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        const companyFields: DynamicField[] = Object.values(companyFieldsMap)
          .map((field) => ({
            id: field.templateReplacementTag,
            label: field.displayName,
            templateTag: `{{ ${field.templateReplacementTag} }}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        const categories: Category[] = [
          {
            id: 'contact',
            label: 'Contact details',
            fields: contactFields,
          },
          {
            id: 'company',
            label: 'Primary company details',
            fields: companyFields,
          },
          ...customObjectCategories,
          accountCategory,
        ];

        if (promotionsEnabled) {
          categories.push(this.couponCategory);
          categories.push(this.promotionCategory);
        }

        return categories;
      }),
      catchError((_) => {
        console.error('Failed to fetch dynamic variable options');
        return of([accountCategory]);
      }),
    );
  }

  convertTemplateTagToHumanReadable$(html$: Observable<string>): Observable<string> {
    const promotions$ = this.activePromotions$ ?? of<ExtraCategoryOption[]>([]);
    return combineLatest([html$, this.allFields$, promotions$, this.customObjectFields$]).pipe(
      map(([html, fieldMappings, promotions, customObjectEntries]) => {
        const promotionMap = new Map(promotions.map((p) => [p.value, p.label]));
        const customObjectTypeNames = this.buildCustomObjectTypeNames(customObjectEntries);
        return this.convertTemplateTagToHumanReadableHelper(html, fieldMappings, promotionMap, customObjectTypeNames);
      }),
    );
  }

  private buildCustomObjectTypeNames(entries: CustomObjectFieldsByType[]): Record<string, string> {
    const namesByTypeID: Record<string, string> = {};
    for (const entry of entries) {
      const rawName = entry.type.singularObjectName || entry.type.pluralObjectName || '';
      const name = rawName.toLowerCase().replace(/\s+/g, '');
      if (entry.type.customObjectTypeId && name) {
        namesByTypeID[entry.type.customObjectTypeId] = name;
      }
    }
    return namesByTypeID;
  }

  getObjectTypePrefixTag(html: string, customObjectTypeNames: Record<string, string> = {}): string {
    if (html.includes(this.objectTypeTags['CustomObject'])) {
      const typeIDMatch = html.match(/CustomObjectTypeID-[^'"\s\]]+/);
      const typeName = typeIDMatch ? customObjectTypeNames[typeIDMatch[0]] : undefined;
      return (typeName || 'customobject') + '.';
    }
    for (const objectTypeTagsKey in this.objectTypeTags) {
      const tagValue = this.objectTypeTags[objectTypeTagsKey];
      if (tagValue && html.includes(tagValue)) {
        return objectTypeTagsKey.toLowerCase() + '.';
      }
    }
    return '';
  }

  convertTemplateTagToHumanReadableHelper(
    html: string,
    fieldMappings: Record<string, CrmDynamicContent>,
    promotionMap = new Map<string, string>(),
    customObjectTypeNames: Record<string, string> = {},
  ): string {
    const fieldIDRegex = /FieldID-([^'"\s\]]+)/;
    const templateTagRegex = /\{\{\s*([^}]+)\s*\}\}/g;
    return html.replace(templateTagRegex, (match, _content: string, offset: number, source: string) => {
      const decodedMatch = this.decodeEncodedTemplateTag(match);
      if (this.isInsideHtmlTag(source, offset)) {
        return decodedMatch;
      }
      const content = decodedMatch.slice(2, -2);
      const trimmedContent = content.trim();
      const fieldIdMatch = trimmedContent.match(fieldIDRegex);
      if ((fieldIdMatch || []).length === 0) {
        const displayText = resolvePromotionChipText(trimmedContent, promotionMap) ?? trimmedContent;
        return `<span contenteditable="false" data-original="${decodedMatch}" style="${this.dynamicFieldSpanStyle}">${displayText}</span>`;
      }

      let displayValue = fieldIdMatch[0] || 'unknown';
      const fieldMatchedName = fieldMappings[fieldIdMatch?.[0]]?.displayName;
      if (fieldMatchedName && fieldMatchedName.length > 0) {
        displayValue = fieldMatchedName
          .trim()
          .split(/[\s/\-\\|&+:;,.<>?!@#$%^*()[\]{}="'`~]+/)
          .filter((word) => word.length > 0)
          .map((word, index) => {
            const cleanWord = word.replace(/[^a-zA-Z0-9]/g, '');
            if (cleanWord.length === 0) return '';
            return index === 0
              ? cleanWord.toLowerCase()
              : cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase();
          })
          .filter((word) => word.length > 0)
          .join('');
      }

      const objectTypePrefix = this.getObjectTypePrefixTag(trimmedContent, customObjectTypeNames);
      return `<span contenteditable="false" data-original="${decodedMatch}" style="${this.dynamicFieldSpanStyle}">${objectTypePrefix}${displayValue}</span>`;
    });
  }

  convertHumanReadableToTemplateTag(html: string): string {
    if (!html) return html;
    const withoutSpans = html.replace(/<span [^>]*data-original="([^"]*)"[^>]*>[^<]*<\/span>/g, '$1');
    return withoutSpans.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match) => this.decodeEncodedTemplateTag(match));
  }

  convertRichTextToPlainTextWithDynamicVariables(html: string): string {
    if (!html) return html;
    let result = this.convertHumanReadableToTemplateTag(html);

    result = result.replace(/<br\s*\/?>/gi, ' ');
    result = result.replace(/<hr\s*\/?>/gi, ' ');

    result = result.replace(/<[^>]*>/g, '');

    result = result
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    result = result.replace(/<(\/?[^>\s]+)(\s[^>]*)?>/g, '< $1$2 >');

    result = result.replace(/\s+/g, ' ').trim();
    return result;
  }
}
